import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Context
import {NavContext} from "../../contexts/nav.js";
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {prettyDateInUserTimezone, toTitleCase} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import BaseTable from "../../components/BaseTable.js";
import Pagination from "../../components/Pagination.js";

// Style
import {pad} from "../../style/components/variables.js";
import {bp, breakpoint} from "../../style/components/breakpoints.js";
import {
  Heading,
  Inline,
  Pill,
  Select,
  TableFooter,
  TableHeaderWrap,
  TableWrapper,
  Text
} from "../../style/components/general.js";

const PAGE_LIMIT = 10;

const HEADINGS = {
  state: {header: "State", disabled: true},
  change: {header: "Change", disabled: true},
  by: {header: "By", disabled: true},
  when: {header: "When", disabled: true}
};

const ModalFacilityStateHistory = ({visible, setVisible, goBack, facility}) => {
  const isMounted = useMountedState();

  const {allStates} = useContext(NavContext);
  const {settings} = useContext(SettingsContext);

  const {api, loading} = useApi("facility-state-history", {suppress: {error: false}});

  const [entries, setEntries] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");

  const stateOptions = useMemo(() => (allStates ? Object.keys(allStates) : []), [allStates]);

  const getHistory = useCallback(() => {
    if (!facility?.id) return;

    const params = {facilityId: facility.id, page: currentPage, limit: PAGE_LIMIT};
    if (filter !== "all" && allStates?.[filter]) params.stateId = allStates[filter].id;

    api.callGet(null, params).then(response => {
      if (!isMounted()) return;

      const {status, data} = response || {};
      if (status === 200 && data) {
        setEntries(data.records);
        setPageTotal(data.pages);
        setTotal(data.total);
      }
    });
  }, [api, facility, allStates, isMounted, currentPage, filter]);

  useEffect(() => {
    getHistory();
  }, [getHistory]);

  const tableData = useMemo(() => {
    if (!entries) return null;
    return entries.map(entry => ({
      state: (
        <Pill color={entry.state?.color ? `#${entry.state.color}` : undefined}>
          {entry.state?.name || "Unknown"}
        </Pill>
      ),
      change: (
        <Action enabled={entry.enabled ? 1 : 0}>
          {entry.enabled ? "Turned On" : "Turned Off"}
        </Action>
      ),
      by: entry.user?.name || "System",
      when: prettyDateInUserTimezone(entry.createdAt, settings?.timezone)
    }));
  }, [entries, settings?.timezone]);

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton
      goBack={goBack}
      width={breakpoint.width[2]}>
      <TableWrapper>
        <TableHeaderWrap>
          <HeaderRow>
            <Heading>State History</Heading>
            <Select
              id="stateHistoryFilter"
              value={filter}
              onChange={e => {
                setFilter(e.target.value);
                setCurrentPage(1);
              }}
              fullWidth>
              <option value="all">All States</option>
              {stateOptions.map(name => (
                <option key={name} value={name}>
                  {toTitleCase(name)}
                </option>
              ))}
            </Select>
          </HeaderRow>
        </TableHeaderWrap>

        <BaseTable
          headings={HEADINGS}
          data={tableData}
          loading={loading}
          dataMissing={
            filter !== "all" ? "No state history for this state." : "No state history recorded yet."
          }
          cellFullWidth
        />

        <TableFooter>
          {total > 0 && (
            <>
              <Text quiet>{total} total</Text>
              {pageTotal > 1 && (
                <Pagination
                  current={currentPage}
                  setCurrent={setCurrentPage}
                  pageTotal={pageTotal}
                  updateData={getHistory}
                  loading={loading}
                />
              )}
            </>
          )}
        </TableFooter>
      </TableWrapper>
    </Modal>
  );
};

ModalFacilityStateHistory.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  facility: PropTypes.objectOf(PropTypes.any).isRequired
};

// Style Overrides
const HeaderRow = styled(Inline)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${pad}px;
  display: block;

  ${bp(1)} {
    display: flex;
  }

  select {
    max-width: 220px;
  }
`;

const Action = styled(Text)`
  font-weight: bold;
  white-space: nowrap;
  color: ${({enabled, theme}) => (enabled ? theme.success : theme.error)};
`;

export default ModalFacilityStateHistory;
